import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import AuthCallback from "@/pages/AuthCallback";
import Landing from "@/pages/Landing";
import TenantLanding from "@/pages/TenantLanding";
import TenantPrivacyPage from "@/pages/TenantPrivacyPage";
import AuthPage from "@/pages/AuthPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import LegalAcceptancePage from "@/pages/LegalAcceptancePage";
import Dashboard from "@/pages/Dashboard";
import ApplyPage from "@/pages/ApplyPage";
import ConsentPage from "@/pages/ConsentPage";
import TestRunner from "@/pages/TestRunner";
import SectionBPage from "@/pages/SectionBPage";
import SectionTransitionPage from "@/pages/SectionTransitionPage";
import CompletionPage from "@/pages/CompletionPage";
import UpdatePage from "@/pages/UpdatePage";
import SuperAdminPage from "@/pages/SuperAdminPage";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import CandidateDetail from "@/pages/admin/CandidateDetail";

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="app-loading">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-[#003366]" />
    </div>
  );
}

function ProtectedRoute({ children, role }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/auth" replace />;
  if (role && user.role !== role) return <Navigate to="/dashboard" replace />;
  if (user.must_change_password && window.location.pathname !== "/cambia-password") return <Navigate to="/cambia-password" replace />;
  if (!user.must_change_password && user.role === "admin" && user.legal_accepted === false && window.location.pathname !== "/accettazione-legale") return <Navigate to="/accettazione-legale" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Check sincrono del fragment PRIMA delle route protette (evita race condition)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/candidati/:slug" element={<TenantLanding />} />
      <Route path="/candidati/:slug/privacy" element={<TenantPrivacyPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/cambia-password" element={<ProtectedRoute><ChangePasswordPage /></ProtectedRoute>} />
      <Route path="/accettazione-legale" element={<ProtectedRoute role="admin"><LegalAcceptancePage /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/candidatura" element={<ProtectedRoute><ApplyPage /></ProtectedRoute>} />
      <Route path="/test/consenso" element={<ProtectedRoute><ConsentPage /></ProtectedRoute>} />
      <Route path="/test" element={<ProtectedRoute><TestRunner /></ProtectedRoute>} />
      <Route path="/test/parte-b" element={<ProtectedRoute><SectionTransitionPage /></ProtectedRoute>} />
      <Route path="/test/sezione-b" element={<ProtectedRoute><SectionBPage /></ProtectedRoute>} />
      <Route path="/test/completamento" element={<ProtectedRoute><CompletionPage /></ProtectedRoute>} />
      <Route path="/aggiornamento/:token" element={<UpdatePage />} />
      <Route path="/superadmin" element={<SuperAdminPage />} />
      <Route path="/admin" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/candidature/:id" element={<ProtectedRoute role="admin"><CandidateDetail /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
